import { IonContent, IonPage } from '@ionic/react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { createUserWithEmailAndPassword, sendEmailVerification } from 'firebase/auth';

import './auth.css';
import { setUserState } from '../redux/actions';
import { auth, writeSignupProfile } from '../firebase';
import {
  BrandPanel,
  Field,
  TextInput,
  PasswordInput,
  PasswordMeter,
  passwordMeetsRule,
  isValidEmail,
  ArrowIcon,
  AlertIcon,
} from './authShared';

const Signup: React.FC = () => {
  const dispatch = useDispatch();

  const [first, setFirst] = useState('');
  const [last, setLast] = useState('');
  const [email, setEmail] = useState('');
  const [facility, setFacility] = useState('');
  const [password, setPassword] = useState('');
  const [agree, setAgree] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const canSubmit = agree && !busy;

  async function registerUser(e?: React.FormEvent) {
    e?.preventDefault();
    if (busy) return;
    if (!isValidEmail(email)) {
      setError('Enter a valid work email.');
      return;
    }
    if (!passwordMeetsRule(password)) {
      setError('Password needs 8+ characters with upper- and lowercase, a number, and a symbol.');
      return;
    }
    if (!agree) {
      setError('Please accept the terms to continue.');
      return;
    }
    setError('');
    setBusy(true);
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      // Fire-and-forget verification email.
      sendEmailVerification(user).catch(() => {});
      dispatch(setUserState(user));
      await writeSignupProfile(user.uid, {
        email,
        firstName: first.trim(),
        lastName: last.trim(),
        facility: facility.trim(),
      });
      // App.tsx role-aware root redirect takes over.
      window.location.href = '/';
    } catch (err: any) {
      if (err?.code === 'auth/email-already-in-use') {
        setError('An account with that email already exists. Try signing in, or contact support@rolloverrep.com.');
      } else {
        setError('Could not create your account. Please try again.');
      }
      // eslint-disable-next-line no-console
      console.log(err?.code, err?.message);
      setBusy(false);
    }
  }

  return (
    <IonPage>
      <IonContent fullscreen className="rr-auth-content">
        <div className="auth">
          <BrandPanel
            eyebrow="get started"
            title={<>The admissions infrastructure for <em>behavioral healthcare.</em></>}
            quote="If your answering service doesn’t know what IOP stands for, they’re doing a disservice to your brand — and more importantly, to help-seekers."
            cite="— Rollover Rep team"
            stats={[
              { num: '$1M+', label: 'in recovered admissions revenue' },
              { num: '30+', label: 'admissions per day' },
              { num: 'HIPAA', label: 'BAA available on every plan' },
            ]}
          />

          <main className="auth-form">
            <div className="auth-form-top">
              <span>Already have an account?</span>
              <Link to="/Login">Sign in</Link>
            </div>

            <div className="auth-form-body">
              <header className="auth-heading">
                <div className="auth-eyebrow">Create an account</div>
                <h2>Create your account.</h2>
                <p>Create your Rollover Rep account to get started.</p>
              </header>

              {error && (
                <div className="auth-banner auth-banner--error">
                  <AlertIcon />
                  <span>{error}</span>
                </div>
              )}

              <form className="auth-fields" onSubmit={registerUser}>
                <div className="auth-row">
                  <Field label="First name">
                    <TextInput value={first} onChange={setFirst} placeholder="Marisol" autoComplete="given-name" />
                  </Field>
                  <Field label="Last name">
                    <TextInput value={last} onChange={setLast} placeholder="Tan" autoComplete="family-name" />
                  </Field>
                </div>

                <Field label="Work email">
                  <TextInput type="email" value={email} onChange={setEmail} placeholder="you@facility.com" autoComplete="email" />
                </Field>

                <Field label="Treatment facility">
                  <TextInput value={facility} onChange={setFacility} placeholder="Cedar Ridge Recovery" />
                </Field>

                <Field label="Password">
                  <PasswordInput value={password} onChange={setPassword} placeholder="Choose a strong password" autoComplete="new-password" />
                  <PasswordMeter pw={password} />
                </Field>

                <label className="auth-check" style={{ alignItems: 'flex-start', lineHeight: 1.5 }}>
                  <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} />
                  <span className="auth-check-box" style={{ marginTop: 2 }} />
                  <span>
                    I agree to the{' '}
                    <a href="https://rolloverrep.com/terms" target="_blank" rel="noreferrer" style={{ color: 'var(--fg-2)' }}>Terms of Service</a>,{' '}
                    <a href="https://rolloverrep.com/privacy" target="_blank" rel="noreferrer" style={{ color: 'var(--fg-2)' }}>Privacy Policy</a>, and{' '}
                    <a href="https://rolloverrep.com/baa" target="_blank" rel="noreferrer" style={{ color: 'var(--fg-2)' }}>Business Associate Agreement</a>.
                  </span>
                </label>

                <button type="submit" className="auth-submit" disabled={!canSubmit}>
                  {busy ? 'Creating account…' : 'Create account'}
                  {!busy && <ArrowIcon />}
                </button>

                <div className="auth-legal">
                  We’ll never share your data. Rollover Rep is SOC 2 Type II and HIPAA‑aligned.
                </div>
              </form>
            </div>
          </main>
        </div>
      </IonContent>
    </IonPage>
  );
};

export default Signup;
